import { Lock, Check } from "lucide-react";
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useCartStore } from '@/stores/cartStore';
import { recordCheckoutNotice, type NoticePlacement } from '@/lib/checkoutReassurance';
import { measurementAllowed } from '@/lib/editorialAttribution';
import { CONSENT_CHANGED_EVENT } from '@/lib/consent';

/**
 * Calm, factual disclosure that Shopify checkout is hosted on our
 * order-processing domain (furnifindusa.com). Shown before the click so the
 * domain change during payment is never a surprise. Not a warning — no alert styling.
 */
const CARDS = ["Visa", "Mastercard", "Amex", "Discover"];

export const CheckoutDomainNotice = ({ className = "", placement = 'cart', compact = false }: { className?: string; placement?: NoticePlacement; compact?: boolean }) => {
  const ref = useRef<HTMLDivElement>(null);
  const cartId = useCartStore(s => s.cartId);
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    setAcknowledged(false);
    const element = ref.current;
    if (!element || !cartId) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let seen = false;
    let visible = false;
    const restart = () => {
      if (timer) clearTimeout(timer);
      timer = undefined;
      if (!measurementAllowed()) seen = false;
      if (!visible || seen || !measurementAllowed() || document.visibilityState !== 'visible') return;
      timer = setTimeout(() => {
        if (!visible || !measurementAllowed() || document.visibilityState !== 'visible') return;
        seen = true;
        recordCheckoutNotice(placement);
      }, 1000);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      restart();
    }, { threshold: [0, 0.5] });
    observer.observe(element);
    window.addEventListener(CONSENT_CHANGED_EVENT, restart);
    document.addEventListener('visibilitychange', restart);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
      window.removeEventListener(CONSENT_CHANGED_EVENT, restart);
      document.removeEventListener('visibilitychange', restart);
    };
  }, [cartId, placement]);
  return <div ref={ref} className={`space-y-2 ${className}`}>
    <div className="flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground">
      <Lock className="h-3.5 w-3.5 mt-[1px] text-primary flex-shrink-0" aria-hidden="true" />
      <p>
        Payment is completed on <span className="font-medium text-foreground">furnifindusa.com</span>, our
        order-processing site. FurniFindUSA appears on the payment page and bank statement — your order is still with LoftLoom.
      </p>
    </div>
    <Button variant="ghost" size="sm" className="h-auto min-h-9 max-w-full whitespace-normal justify-start text-left text-xs" aria-pressed={acknowledged} onClick={() => {
      setAcknowledged(true); recordCheckoutNotice(placement, true);
    }}>{acknowledged && <Check className="mr-1 h-3.5 w-3.5 shrink-0" />}{acknowledged ? 'Understood — payment is with FurniFindUSA' : 'I understand payment is with FurniFindUSA'}</Button>
    {!compact && <ul aria-label="Accepted cards" className="flex flex-wrap items-center gap-1.5 pl-5">
      {CARDS.map((c) => (
        <li key={c} className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-foreground/80">
          {c}
        </li>
      ))}
      <li className="text-[10px] text-muted-foreground">· SSL encrypted</li>
    </ul>}
  </div>;
};
